// Locations page — vanilla JS, no dependencies.
//
// Reads /assets/locations.json (uploaded by the nightly sync), renders the
// Bottles + Cocktails sections grouped by city, and wires up the toggle
// and search filter.

(function () {
  const root = document.querySelector("[data-locations-page]");
  if (!root) return;

  const assetUrl = root.dataset.assetUrl;
  const defaultSection = root.dataset.defaultSection || "bottles";
  const status = root.querySelector("[data-status]");
  const meta = root.querySelector("[data-meta]");
  const toggleButtons = root.querySelectorAll("[data-toggle]");
  const searchInput = root.querySelector("#locations-search");
  const sections = {
    bottles: {
      panel: root.querySelector('[data-section="bottles"]'),
      list: root.querySelector('[data-list="bottles"]'),
      empty: root.querySelector('[data-empty="bottles"]'),
      data: [],
    },
    cocktails: {
      panel: root.querySelector('[data-section="cocktails"]'),
      list: root.querySelector('[data-list="cocktails"]'),
      empty: root.querySelector('[data-empty="cocktails"]'),
      data: [],
    },
  };

  let activeSection = defaultSection;
  let searchTerm = "";

  // --- Render helpers ----------------------------------------------------

  function groupByCity(locations) {
    const groups = new Map();
    for (const loc of locations) {
      const city = (loc.city || "Other").trim() || "Other";
      if (!groups.has(city)) groups.set(city, []);
      groups.get(city).push(loc);
    }
    const sorted = [...groups.entries()]
      .map(([city, items]) => ({
        city,
        items: items.sort((a, b) =>
          (a.name || "").localeCompare(b.name || ""),
        ),
      }))
      .sort((a, b) => a.city.localeCompare(b.city));
    return sorted;
  }

  function matchesSearch(loc, term) {
    if (!term) return true;
    const haystack = `${loc.name || ""} ${loc.city || ""} ${loc.address || ""}`.toLowerCase();
    return haystack.includes(term);
  }

  function renderSection(key) {
    const section = sections[key];
    const filtered = section.data.filter((l) => matchesSearch(l, searchTerm));
    const grouped = groupByCity(filtered);

    if (filtered.length === 0) {
      section.list.innerHTML = "";
      section.empty.hidden = false;
      return;
    }
    section.empty.hidden = true;

    const html = grouped
      .map((group) => {
        const items = group.items
          .map((loc) => {
            const name = escapeHtml(toTitleCase(loc.name));
            const address = escapeHtml(toTitleCase(loc.address));
            const mapsUrl = encodeURI(loc.mapsUrl || "");
            return `
              <li class="locations-page__location">
                <h3 class="locations-page__location-name">${name}</h3>
                <p class="locations-page__location-address">${address}</p>
                <a
                  class="locations-page__directions button button--secondary"
                  href="${mapsUrl}"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get directions to ${name}"
                >Get Directions</a>
              </li>
            `;
          })
          .join("");
        return `
          <div class="locations-page__city">
            <h2 class="locations-page__city-name">${escapeHtml(group.city)}</h2>
            <ul class="locations-page__locations">${items}</ul>
          </div>
        `;
      })
      .join("");

    section.list.innerHTML = html;
  }

  function renderAll() {
    renderSection("bottles");
    renderSection("cocktails");
  }

  function showSection(key) {
    activeSection = key;
    for (const [sectionKey, s] of Object.entries(sections)) {
      s.panel.hidden = sectionKey !== key;
    }
    for (const btn of toggleButtons) {
      const selected = btn.dataset.toggle === key;
      btn.setAttribute("aria-selected", selected ? "true" : "false");
    }
  }

  function showStatus(text, isError) {
    status.hidden = false;
    status.innerHTML = `<p class="${isError ? "locations-page__error" : "locations-page__loading"}">${escapeHtml(text)}</p>`;
  }

  function hideStatus() {
    status.hidden = true;
  }

  function escapeHtml(s) {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // Normalize text to Title Case while preserving common uppercase
  // abbreviations (US state codes, country codes, etc.) so that addresses
  // and names look consistent regardless of how they were entered in Airtable.
  const KEEP_UPPER = new Set([
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
    "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
    "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
    "VA","WA","WV","WI","WY",
    "USA","US","DC","LLC","LLP","INC","LTD","BBQ","BYOB",
  ]);
  function toTitleCase(s) {
    if (s == null) return "";
    // Lowercase the whole string, then re-capitalize each word's first letter.
    // The regex [\w']+ keeps apostrophes inside words ("Chimy's" not "Chimy'S").
    const titled = String(s)
      .toLowerCase()
      .replace(/[\w']+/g, (word) =>
        word.charAt(0).toUpperCase() + word.slice(1),
      );
    // Restore well-known uppercase abbreviations.
    return titled.replace(/\b[A-Za-z]{2,4}\b/g, (m) => {
      const up = m.toUpperCase();
      return KEEP_UPPER.has(up) ? up : m;
    });
  }

  function formatLastUpdated(iso) {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "";
    }
  }

  // --- Wire up events ----------------------------------------------------

  for (const btn of toggleButtons) {
    btn.addEventListener("click", () => {
      showSection(btn.dataset.toggle);
    });
  }

  if (searchInput) {
    let debounce;
    searchInput.addEventListener("input", () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        searchTerm = searchInput.value.trim().toLowerCase();
        renderAll();
      }, 120);
    });
  }

  // --- Fetch data --------------------------------------------------------

  async function load() {
    try {
      // Cache-bust so changes appear quickly after a sync.
      const url = `${assetUrl}?t=${Date.now()}`;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      sections.bottles.data = Array.isArray(data?.sections?.bottles)
        ? data.sections.bottles
        : [];
      sections.cocktails.data = Array.isArray(data?.sections?.cocktails)
        ? data.sections.cocktails
        : [];

      if (
        sections.bottles.data.length === 0 &&
        sections.cocktails.data.length === 0
      ) {
        showStatus("No locations are available yet. Please check back soon.", false);
        return;
      }

      hideStatus();
      showSection(defaultSection);
      renderAll();

      const lastUpdated = formatLastUpdated(data.lastSynced);
      if (lastUpdated) {
        meta.hidden = false;
        meta.textContent = `Last updated ${lastUpdated}`;
      }
    } catch (e) {
      console.error("Locations load failed:", e);
      showStatus(
        "We can't load the locations list right now. Please refresh or try again later.",
        true,
      );
    }
  }

  load();
})();
